import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import logo0 from '../assets/logo0.png';
import owlIcon from '../assets/logo00.png';
import handIcon from '../assets/logo00.png';
import headsetIcon from '../assets/logo00.png';
import repair from '../assets/repair.jpg';
import { keyframes } from '@mui/material/styles';

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const Services = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <>
      {/* Social Icons Bar */}
      <Box sx={{ 
        bgcolor: '#000', 
        py: 1,
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box>
              {isMobile ? (
                <IconButton color="inherit" sx={{ color: '#FFE302' }}>
                  <MenuIcon />
                </IconButton>
              ) : (
                <Typography variant="body2" sx={{ color: '#fff' }}>
                  Your Trusted AI-Powered Repair Solution
                </Typography>
              )}
            </Box>
            
            <Box sx={{ display: 'flex' }}>
              {[
                { icon: <FacebookIcon />, label: 'Facebook' },
                { icon: <TwitterIcon />, label: 'Twitter' },
                { icon: <InstagramIcon />, label: 'Instagram' },
                { icon: <LinkedInIcon />, label: 'LinkedIn' }
              ].map((social, index) => (
                <IconButton 
                  key={index}
                  aria-label={social.label}
                  sx={{ 
                    color: '#fff',
                    '&:hover': { color: '#FFE302' }
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Compact Hero Section with Stylish Circles */}
      <Box sx={{ 
        bgcolor: '#FFE302',
        py: { xs: 3, md: 5 },
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Circles */}
        <Box sx={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          bgcolor: '#000',
          opacity: 0.8,
          top: '-150px',
          right: '-50px',
          zIndex: 1
        }} />
        
        <Box sx={{
          position: 'absolute',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          bgcolor: '#000',
          opacity: 0.6,
          bottom: '-100px',
          left: '10%',
          zIndex: 1
        }} />
        
        <Box sx={{
          position: 'absolute',
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          bgcolor: '#000',
          opacity: 0.7,
          top: '20%',
          left: '5%',
          zIndex: 1
        }} />
        
        <Box sx={{
          position: 'absolute',
          width: '150px',
          height: '150px',
          borderRadius: '50%',
          bgcolor: '#000',
          opacity: 0.5,
          bottom: '10%',
          right: '15%',
          zIndex: 1
        }} />
        
        <Box sx={{
          position: 'absolute',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          bgcolor: '#000',
          opacity: 0.4,
          top: '40%',
          right: '30%',
          zIndex: 1
        }} />
        
        {/* Diagonal Lines */}
        <Box sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.05), rgba(0,0,0,0.05) 10px, transparent 10px, transparent 20px)',
          zIndex: 1
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'center', md: 'flex-start' },
            justifyContent: 'space-between'
          }}>
            {/* Left Content */}
            <Box sx={{ 
              maxWidth: { xs: '100%', md: '50%' },
              textAlign: { xs: 'center', md: 'left' },
              mb: { xs: 4, md: 0 },
              position: 'relative',
              zIndex: 2
            }}>
              <Box 
                component="img"
                src={logo0}
                alt="Warrili"
                sx={{ 
                  width: '120px',
                  mb: 3
                }}
              />

              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '2rem', md: '2.8rem' },
                  fontWeight: 900,
                  color: '#000',
                  mb: 2,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                  fontFamily: '"Poppins", sans-serif',
                }}
              >
                Your Trusted AI-Powered Repair Solution
              </Typography>

              <Box sx={{ 
                borderLeft: '4px solid #000',
                pl: { xs: 2, md: 3 },
                mb: 4,
                maxWidth: '600px'
              }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    color: '#000',
                    fontWeight: 500,
                    lineHeight: 1.6,
                    fontFamily: '"Inter", sans-serif',
                    '& strong': {
                      fontWeight: 700
                    }
                  }}
                >
                  Your go-to AI-driven platform for <strong>smart device diagnostics</strong> and repair solutions. Our advanced AI technology identifies hardware and software issues instantly, providing <strong>step-by-step guidance</strong> for fixing them.
                </Typography>
              </Box>

              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'flex-start' },
              }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: '#000',
                    color: '#FFE302',
                    px: 4,
                    py: 1.5,
                    borderRadius: 0,
                    '&:hover': {
                      bgcolor: '#333'
                    },
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '1rem',
                    letterSpacing: '0.5px',
                    fontFamily: '"Inter", sans-serif'
                  }}
                >
                  CONTACT US
                </Button>

                <Button
                  variant="outlined"
                  sx={{
                    bgcolor: 'transparent',
                    borderColor: '#000',
                    color: '#000',
                    px: 4,
                    py: 1.5,
                    borderRadius: 0,
                    borderWidth: 2,
                    '&:hover': {
                      bgcolor: '#000',
                      borderColor: '#000',
                      color: '#FFE302'
                    },
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '1rem',
                    letterSpacing: '0.5px',
                    fontFamily: '"Inter", sans-serif'
                  }}
                >
                  GET STARTED
                </Button>
              </Box>
            </Box>

            {/* Right Illustration - Floating Animation */}
            <Box sx={{ 
              position: 'relative',
              width: { xs: '100%', md: '45%' },
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              alignItems: 'center',
              maxHeight: { xs: '250px', md: '400px' },
              overflow: 'visible'
            }}>
              {/* Main Image Container */}
              <Box sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                animation: `${floatAnimation} 3s ease-in-out infinite`,
                '&:hover': {
                  '& img': {
                    transform: 'scale(1.05) rotate(1deg)',
                  }
                }
              }}>
                <Box
                  component="img"
                  src={repair}
                  alt="Professional Repair Service"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxWidth: '500px',
                    objectFit: 'contain',
                    transition: 'transform 0.6s ease',
                    filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.15))',
                  }}
                />

                {/* Decorative Circles */}
                <Box sx={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  border: '2px solid #FFE302',
                  opacity: 0.3,
                  animation: `${floatAnimation} 3s ease-in-out infinite`,
                  animationDelay: '0.5s'
                }} />
                
                <Box sx={{
                  position: 'absolute',
                  bottom: -30,
                  left: -30,
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  border: '2px solid #FFE302',
                  opacity: 0.3,
                  animation: `${floatAnimation} 3s ease-in-out infinite`,
                  animationDelay: '1s'
                }} />

                {/* Yellow Accent Lines */}
                <Box sx={{
                  position: 'absolute',
                  top: '30%',
                  left: -20,
                  width: '40px',
                  height: '3px',
                  bgcolor: '#FFE302',
                  opacity: 0.7,
                  animation: `${floatAnimation} 3s ease-in-out infinite`,
                  animationDelay: '0.2s'
                }} />

                <Box sx={{
                  position: 'absolute',
                  bottom: '40%',
                  right: -20,
                  width: '40px',
                  height: '3px',
                  bgcolor: '#FFE302',
                  opacity: 0.7,
                  animation: `${floatAnimation} 3s ease-in-out infinite`,
                  animationDelay: '0.7s'
                }} />
              </Box>

              <Typography 
                variant="body2" 
                align="center"
                sx={{ 
                  position: 'absolute',
                  bottom: -40,
                  width: '100%',
                  color: '#000',
                  fontWeight: 700,
                  fontSize: { xs: '0.8rem', md: '0.9rem' },
                }}
              >
                Download our free eBook
                <br />
                or listen to the audiobook
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Services Section - Clean and Simple Design */}
      <Box sx={{ 
        bgcolor: '#111', 
        color: 'white', 
        py: { xs: 8, md: 10 },
        borderTop: '1px solid rgba(255,227,2,0.1)'
      }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h2" 
            align="left"
            sx={{ 
              fontWeight: 900,
              mb: { xs: 5, md: 6 },
              fontSize: { xs: '2.2rem', md: '3rem' },
              pl: 2,
              borderLeft: '4px solid #FFE302'
            }}
          >
            Our Services
          </Typography>

          <Grid container spacing={2}>
            {[
              {
                number: '01',
                title: 'AI-Powered Diagnostics',
                description: 'Advanced artificial intelligence systems that accurately diagnose device issues within minutes. Get detailed reports and step-by-step solutions'
              },
              {
                number: '02',
                title: 'Expert Repair Service',
                description: 'Professional technicians with years of experience provide top-tier repair services for all your devices and technical needs'
              },
              {
                number: '03',
                title: '24/7 Support',
                description: 'Access round-the-clock customer support for immediate assistance. Our team is always ready to help resolve your technical issues'
              },
              {
                number: '04',
                title: 'Quality Guarantee',
                description: 'Every repair comes with our satisfaction guarantee. We ensure your devices work perfectly or we will fix them again at no cost'
              }
            ].map((service, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box 
                  sx={{ 
                    position: 'relative',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#000',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      '& .number': {
                        color: '#FFE302'
                      }
                    }
                  }}
                >
                  <Box sx={{
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    position: 'relative'
                  }}>
                    <Typography 
                      className="number"
                      sx={{
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: 'rgba(255,255,255,0.3)',
                        mb: 2,
                        transition: 'color 0.3s ease'
                      }}
                    >
                      {service.number}
                    </Typography>

                    <Typography 
                      variant="h5" 
                      sx={{ 
                        mb: 3,
                        fontWeight: 700,
                        color: '#fff',
                        fontSize: { xs: '1.5rem', md: '1.8rem' },
                        lineHeight: 1.2
                      }}
                    >
                      {service.title}
                    </Typography>
                    
                    <Typography 
                      variant="body1"
                      sx={{ 
                        color: 'rgba(255,255,255,0.7)',
                        lineHeight: 1.8,
                        fontSize: '1rem',
                        mb: 4,
                        flexGrow: 1
                      }}
                    >
                      {service.description}
                    </Typography>

                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                          '& .arrow': {
                            transform: 'translateX(8px)'
                          },
                          '& .text': {
                            color: '#FFE302'
                          }
                        }
                      }}
                    >
                      <Typography 
                        className="text"
                        sx={{
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: '#fff',
                          transition: 'color 0.3s ease',
                          mr: 1
                        }}
                      >
                        Learn more
                      </Typography>
                      <Box 
                        className="arrow"
                        component="span" 
                        sx={{
                          transition: 'transform 0.3s ease',
                          display: 'inline-block'
                        }}
                      >
                        →
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '1px',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Services;
